import { useState, useRef, useEffect } from "react";

export default function AsyncSearchableSelect({
  value,
  onChange,
  loadOptions,
  placeholder = "Select...",
  labelKey = "label",
  valueKey = "value",
  selectedLabel // optional (for edit mode)
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const latestRequestRef = useRef(0);

  // Find selected option
  const selected = options.find(o => o[valueKey] === value);
  const displayLabel = selected?.[labelKey] || selectedLabel || "";

  // 🔹 Fetch options (debounced + race safe)
  useEffect(() => {
    if (!loadOptions) return;

    const requestId = ++latestRequestRef.current;

    const delay = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await loadOptions(search);

        // Ignore stale responses
        if (requestId === latestRequestRef.current) {
          setOptions(res || []);
        }
      } catch {
        if (requestId === latestRequestRef.current) {
          setOptions([]);
        }
      } finally {
        if (requestId === latestRequestRef.current) {
          setLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [search, loadOptions]);

  // 🔹 Initial load when dropdown opens
  useEffect(() => {
    if (!open || !loadOptions) return;

    const fetchInitial = async () => {
      setLoading(true);
      try {
        const res = await loadOptions("");
        setOptions(res || []);
      } finally {
        setLoading(false);
      }
    };

    fetchInitial();
  }, [open, loadOptions]);

  // 🔹 Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // 🔹 Focus search input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    }
  }, [open]);

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Trigger */}
      <div
        onClick={() => {
          setOpen(prev => !prev);
          setSearch("");
        }}
        className="w-full px-3 py-2 border border-slate-200 bg-white cursor-pointer flex items-center justify-between hover:border-slate-300 transition"
      >
        <span className={displayLabel ? "text-slate-800" : "text-slate-400"}>
          {displayLabel || placeholder}
        </span>

        {/* Arrow */}
        <svg
          className={`w-4 h-4 text-slate-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 w-full bg-white border border-slate-200 mt-1 shadow-lg rounded-md overflow-hidden">
          
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 border border-slate-200 rounded outline-none focus:ring-1 focus:ring-brand-accent"
            />
          </div>

          {/* Options */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-sm text-slate-500">
                Loading...
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500">
                No results
              </div>
            ) : (
              options.map((option) => (
                <div
                  key={option[valueKey]}
                  onClick={() => {
                    onChange(option[valueKey]);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="px-3 py-2 cursor-pointer hover:bg-slate-100 transition"
                >
                  {option[labelKey]}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}